import type { ReactNode } from "react";

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="screen">
      <div className="container">
        <BackRow onClick={onClose} />
        <div className="screen-title">{title.toUpperCase()}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{children}</div>
      </div>
    </div>
  );
}

export function BackRow({ onClick }: { onClick: () => void }) {
  return (
    <button className="backrow" onClick={onClick} aria-label="Back">
      <span className="chev" aria-hidden="true">‹</span> BACK
    </button>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%", background: "var(--panel2)", border: "1px solid var(--edge)", borderRadius: 10,
  color: "var(--cream)", fontFamily: "var(--body)", fontSize: 15, padding: "12px 14px",
};
const labelStyle: React.CSSProperties = { fontFamily: "var(--mono)", fontSize: 10, letterSpacing: 1.4, color: "var(--muted)", textTransform: "uppercase", marginBottom: 5, display: "block" };

export function TextField({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label>
      <span style={labelStyle}>{label}</span>
      <input style={inputStyle} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

export function NumberField({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label>
      <span style={labelStyle}>{label}</span>
      <input
        style={inputStyle}
        value={value}
        placeholder={placeholder}
        inputMode="decimal"
        onChange={(e) => onChange(e.target.value.replace(",", ".").replace(/[^0-9.]/g, ""))}
      />
    </label>
  );
}

export function SelectField({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <label>
      <span style={labelStyle}>{label}</span>
      <select style={inputStyle} value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

export function DateField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label>
      <span style={labelStyle}>{label}</span>
      <input type="date" style={inputStyle} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

export function CheckboxField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <span style={{ ...labelStyle, marginBottom: 0 }}>{label}</span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ width: 20, height: 20, accentColor: "var(--amber)" }} />
    </label>
  );
}

export function FormActions({ onSave, onDelete, saveLabel = "SAVE", canSave = true }: { onSave: () => void; onDelete?: () => void; saveLabel?: string; canSave?: boolean }) {
  return (
    <div style={{ display: "flex", gap: 10, marginTop: 6, alignItems: "center" }}>
      {onDelete && <button className="btn ghost" style={{ borderColor: "var(--redline)", color: "var(--redline)" }} onClick={onDelete}>DELETE</button>}
      <div style={{ flex: 1 }} />
      <button className="btn primary" disabled={!canSave} style={{ opacity: canSave ? 1 : 0.5 }} onClick={onSave}>{saveLabel}</button>
    </div>
  );
}

export const parseNum = (s: string): number => { const n = parseFloat(s); return Number.isFinite(n) ? n : 0; };
// Round to at most 2 decimals for display in forms (no forced trailing zeros).
export const numToStr = (n: number | null | undefined): string => (n == null ? "" : String(Math.round(n * 100) / 100));
